export interface AppearanceOption {
  value: string;
  label: string;
  swatch: string;
  swatchBg?: string;
}

export const APPEARANCE_SETTINGS = [
  {
    key: 'lightTheme',
    attribute: 'data-light',
    label: 'Thème clair',
    options: [
      { value: 'chaud', label: 'Sable', swatch: '#322d28', swatchBg: '#f7f5f1' },
      { value: 'anthracite', label: 'Anthracite', swatch: '#2a3238', swatchBg: '#f4f6f7' },
    ],
  },
  {
    key: 'lightAccent',
    attribute: 'data-accent',
    label: 'Accent (thème clair)',
    options: [
      { value: 'anthracite', label: 'Anthracite', swatch: '#322d28' },
      { value: 'anthracite-titres', label: 'Anthracite + titres', swatch: '#2a3238' },
      { value: 'cobalt', label: 'Cobalt', swatch: '#2457c5' },
      { value: 'petrole', label: 'Pétrole', swatch: '#0d6577' },
      { value: 'marine', label: 'Marine', swatch: '#1f3a6b' },
      { value: 'electrique', label: 'Électrique', swatch: '#0057e0' },
      { value: 'turquoise', label: 'Turquoise', swatch: '#0e7490' },
      { value: 'framboise', label: 'Framboise', swatch: '#be185d' },
      { value: 'prune', label: 'Prune', swatch: '#6b2160' },
      { value: 'orange', label: 'Orange Finvens assombri', swatch: '#c03a1b' },
      { value: 'emeraude', label: 'Vert émeraude', swatch: '#007a50' },
    ],
  },
  {
    key: 'warningColor',
    attribute: 'data-warn',
    label: 'Avertissement',
    options: [
      { value: 'ambre-profond', label: 'Ambre profond', swatch: '#9c5c06', swatchBg: '#f8efdb' },
      { value: 'orange', label: 'Orange', swatch: '#e84b29', swatchBg: '#fdeae3' },
    ],
  },
  {
    key: 'darkTheme',
    attribute: 'data-dark',
    label: 'Thème sombre',
    options: [
      { value: 'anthracite', label: 'Anthracite', swatch: '#cfdae2', swatchBg: '#10161a' },
      { value: 'encre', label: 'Encre', swatch: '#ddd9e6', swatchBg: '#18171d' },
    ],
  },
] as const satisfies ReadonlyArray<{ key: string; attribute: `data-${string}`; label: string; options: readonly AppearanceOption[] }>;

type SettingSpec = (typeof APPEARANCE_SETTINGS)[number];
export type AppearanceSetting = SettingSpec['key'];
type ValueOf<K extends AppearanceSetting> = Extract<SettingSpec, { key: K }>['options'][number]['value'];

const COLOR_MODES = ['light', 'dark'] as const;
type ColorMode = (typeof COLOR_MODES)[number];

type AppearanceSettings = { [K in AppearanceSetting]: ValueOf<K> };

export interface Appearance extends AppearanceSettings {
  colorMode: ColorMode;
}

export type AppearancePatch = { [K in keyof Appearance]?: Appearance[K] | null };

export const DEFAULT_SETTINGS: Readonly<AppearanceSettings> = Object.freeze({
  lightTheme: 'chaud',
  lightAccent: 'anthracite',
  warningColor: 'ambre-profond',
  darkTheme: 'anthracite',
});

export const DEFAULT_APPEARANCE: Readonly<Appearance> = Object.freeze({ ...DEFAULT_SETTINGS, colorMode: 'light' });

const ALLOWED: Record<keyof Appearance, readonly string[]> = {
  ...Object.fromEntries(APPEARANCE_SETTINGS.map(spec => [spec.key, spec.options.map(option => option.value)])) as Record<AppearanceSetting, string[]>,
  colorMode: COLOR_MODES,
};

export function normalizeAppearance (raw: unknown, fallback: Readonly<Appearance> = DEFAULT_APPEARANCE): Appearance {
  const source = (raw !== null && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return Object.fromEntries((Object.keys(ALLOWED) as Array<keyof Appearance>).map((key) => {
    const value = source[key];
    return [key, typeof value === 'string' && ALLOWED[key].includes(value) ? value : fallback[key]];
  })) as unknown as Appearance;
}

type AppearanceAttribute = 'data-theme' | SettingSpec['attribute'];

export function appearanceAttributes (appearance: Appearance): Record<AppearanceAttribute, string | null> {
  const dark = appearance.colorMode === 'dark';
  const attributes = { 'data-theme': dark ? 'dark' : null } as Record<AppearanceAttribute, string | null>;
  for (const { key, attribute } of APPEARANCE_SETTINGS) {
    attributes[attribute] = appearance[key] === DEFAULT_SETTINGS[key] ? null : appearance[key];
  }
  if (dark) { attributes['data-accent'] = null; }
  return attributes;
}

type LightAppearanceAttribute = 'data-light' | 'data-accent' | 'data-warn';

export function lightAppearanceAttributes (appearance: Appearance): Record<LightAppearanceAttribute, string | null> {
  const attributes = appearanceAttributes({ ...appearance, colorMode: 'light' });
  return { 'data-light': attributes['data-light'], 'data-accent': attributes['data-accent'], 'data-warn': attributes['data-warn'] };
}
